import type { KitManifest } from '../../shared/discoverKits';

const manifest: KitManifest = {
    slug: 'optimisations',
    context: 'size2mix',
    name: 'Optimisations',
    description:
        "Visualisation et configuration d'une optimisation Nemo (trajectoire, simulation, replay) : synthèse KPI, graphes de résultats, allocation production/consommation, mix énergétique, coûts, slacks et logs, ainsi que la création/modification (paramètres, données d'entrée, plannings).",
    status: 'partial',
    owner: 'platform-team',
    tags: [
        'optimisation',
        'trajectory',
        'simulation',
        'replay',
        'charts',
        'kpi',
        'cost',
        'energy-mix',
        'slacks',
        'logs',
    ],
    nemoSources: [
        {
            label: 'Page racine résultats',
            path: 'apps/frontend-next/src/ui/views/pages/network/optimisations/index.tsx',
            note: "Composition multi-onglets de la vue résultats d'une optimisation.",
        },
        {
            label: 'Synthèse KPI',
            path: 'apps/frontend-next/src/ui/views/pages/network/optimisations/synthesis/',
            note: 'OptimisationSynthesis + OptimisationKpiTables. KPIs cards + tables agrégées.',
        },
        {
            label: 'Charts de résultats',
            path: 'apps/frontend-next/src/ui/views/pages/network/optimisations/charts/',
            note: 'Allocation production/consommation, mix énergétique, coûts, départs, criticité, sizing pipes…',
        },
        {
            label: 'Cartographic results',
            path: 'apps/frontend-next/src/ui/views/pages/network/optimisations/CartographicResults.tsx',
            note: 'Vue carte des résultats avec popup de détail.',
        },
        {
            label: 'Logs & slacks',
            path: 'apps/frontend-next/src/ui/views/pages/network/optimisations/logs/',
            note: 'ErrorsLogs, LimitingFactorsTable, OptimisationSlacksSummary/Details/Tables.',
        },
        {
            label: 'Paramètres (édition)',
            path: 'apps/frontend-next/src/ui/views/pages/network/optimisations/parameters/',
            note: "Onglets de configuration : Général, Données d'entrée, Stockages, Plannings production/topologie, Options avancées.",
        },
        {
            label: 'Création',
            path: 'apps/frontend-next/src/ui/views/pages/network/optimisations/actions/',
            note: 'CreateOptimisationContainer + menus de création (hot water / cold water) + DeleteOptimisationModal.',
        },
        {
            label: 'Layout sidebar',
            path: 'apps/frontend-next/src/ui/views/layout/network/OptimisationLayout.tsx',
            note: 'Layout avec sidebar latérale OptiSidebarMenu propre aux optimisations.',
        },
        {
            label: 'Messages de statut',
            path: 'apps/frontend-next/src/ui/views/pages/network/optimisations/RunningOptimisationMessage.tsx',
            note: 'Aussi : FailedOptimisationMessage, OptimisationLegacyMessageLink. États transverses.',
        },
        {
            label: 'Renderers AG Grid',
            path: 'apps/frontend-next/src/ui/views/pages/network/optimisations/renderers/',
            note: 'Cell renderers spécifiques aux tables des optimisations.',
        },
    ],
    sharedComponents: [
        '@shared/PrototypeStudioLayout (chrome de page)',
        '@shared/DataTable (toutes les tables)',
        '@shared/charts/MixedChart (production allocation, energy mix, cost, criticité, sizing)',
        '@shared/charts/BarChart (cost overall, slacks summary)',
        '@shared/charts/LineChart (trends, departures)',
        '@shared/charts/DoughnutChart (mix overview)',
        '@shared/SegmentedControl (switch granularité ou type de vue)',
        '@shared/AccordionItem (sections rétractables côté paramètres)',
        '@shared/modal/CenterModal (DeleteOptimisationModal-like)',
        '@shared/modal/RightSideModal (panneaux de détail)',
    ],
    views: [
        {
            id: 'synthesis-kpis',
            label: 'Résultats — Synthèse KPI',
            description:
                "Vue d'entrée : KPIs principaux (coût total, CO2, mix, dispo) en cartes + tables agrégées.",
            screenshot: '01-synthesis-kpis.png',
        },
        {
            id: 'production-allocation-chart',
            label: 'Résultats — Allocation production',
            description:
                'Graphe stacked bar/line de la production allouée par actif sur la période. Switch granularité (heure/jour/mois).',
            screenshot: '02-production-allocation-chart.png',
        },
        {
            id: 'production-consumption-chart',
            label: 'Résultats — Allocation production vs consommation',
            description:
                'Graphe combiné production / consommation / stockage. Vue analytique principale.',
            screenshot: '03-production-consumption-chart.png',
        },
        {
            id: 'energy-mix-chart',
            label: 'Résultats — Mix énergétique',
            description:
                'Stacked area + doughnut overview du mix par source. Tooltips items en français.',
            screenshot: '04-energy-mix-chart.png',
        },
        {
            id: 'cost-allocation',
            label: 'Résultats — Allocation des coûts',
            description:
                'Vue détaillée + overall des coûts (carburants, opex, capex, pénalités). 2 sous-vues : detailed / overall.',
            screenshot: '05-cost-allocation.png',
        },
        {
            id: 'departures',
            label: 'Résultats — Départs',
            description:
                'Tableau des départs (DeparturesTable) + chart. Côté hot-water network principalement.',
            screenshot: '06-departures.png',
        },
        {
            id: 'cartographic-results',
            label: 'Résultats — Cartographie',
            description:
                'Vue carte du réseau avec couleurs/épaisseurs encodant les résultats + popup au clic.',
            screenshot: '07-cartographic-results.png',
        },
        {
            id: 'critical-path',
            label: 'Résultats — Chemin critique',
            description:
                'Chart spécifique CriticalPathChart. Identifie les contraintes actives.',
            screenshot: '08-critical-path.png',
        },
        {
            id: 'pipes-sizing',
            label: 'Résultats — Sizing pipes',
            description:
                'Chart de dimensionnement des conduites. Spécifique hot-water.',
            screenshot: '09-pipes-sizing.png',
        },
        {
            id: 'slacks-summary',
            label: 'Logs — Slacks summary',
            description:
                'Vue résumée des contraintes relâchées (slacks) avec drill-down vers détails.',
            screenshot: '10-slacks-summary.png',
        },
        {
            id: 'errors-logs',
            label: 'Logs — Erreurs & limitants',
            description:
                "ErrorsLogs + LimitingFactorsTable. Diagnostic d'une optimisation échouée ou contrainte.",
            screenshot: '11-errors-logs.png',
        },
        {
            id: 'parameters-general',
            label: 'Paramètres — Informations générales',
            description:
                'Premier onglet de la création/édition : nom, type, période, dates de début/fin.',
            screenshot: '12-parameters-general.png',
        },
        {
            id: 'parameters-input-data',
            label: "Paramètres — Données d'entrée",
            description:
                'Sélection des fichiers/datasets de prévisions, prix, consommations.',
            screenshot: '13-parameters-input-data.png',
        },
        {
            id: 'parameters-storages',
            label: 'Paramètres — Stockages',
            description:
                "Configuration des états initiaux/finaux des stockages thermiques.",
            screenshot: '14-parameters-storages.png',
        },
        {
            id: 'parameters-plannings',
            label: 'Paramètres — Plannings',
            description:
                'ProductionPlannings + TopologyPlannings : disponibilités planifiées des actifs.',
            screenshot: '15-parameters-plannings.png',
        },
        {
            id: 'parameters-advanced',
            label: 'Paramètres — Options avancées',
            description:
                'AdvancedOptions : tolérances, solveur, pas de temps, options expert.',
            screenshot: '16-parameters-advanced.png',
        },
        {
            id: 'create-menu',
            label: 'Création — Menu de création',
            description:
                'OptimisationCreateMenu : choix du type (trajectoire, simulation, replay) selon hot/cold water.',
            screenshot: '17-create-menu.png',
        },
        {
            id: 'delete-modal',
            label: 'Suppression — Modale de confirmation',
            description: 'DeleteOptimisationModal. Pattern modale destructive standard.',
            screenshot: '18-delete-modal.png',
        },
        {
            id: 'running-message',
            label: 'État — Optimisation en cours',
            description:
                "RunningOptimisationMessage : état d'attente avec spinner et progression.",
            screenshot: '19-running-message.png',
        },
        {
            id: 'failed-message',
            label: 'État — Optimisation échouée',
            description:
                'FailedOptimisationMessage : message d\'erreur + lien vers les logs.',
            screenshot: '20-failed-message.png',
        },
    ],
    notes: `
- L'area est la plus riche du studio : 20 vues identifiées. Quand l'utilisateur demande
  un proto "optimisation", TOUJOURS lui demander quelle vue précise (et lui montrer la
  liste depuis /kits/optimisations).
- Hot-water vs cold-water : certaines vues (departures, pipes-sizing, cartographic) sont
  hot-water uniquement. Le manifest les marque dans la description, à respecter.
- Trajectoire vs Simulation vs Replay : trois types d'optimisation. Les paramètres
  diffèrent légèrement. Voir HotWaterMenuItems / ColdWaterMenuItems pour la matrice.
- Les charts utilisent Chart.js + nos wrappers. Aucune lib externe (pas de Recharts, pas
  de Highcharts).
- Tables : DataTable AG Grid uniquement. Les renderers/ contiennent des cellRenderer
  spécifiques (statut, lien vers détails) à mimer.
- Pour un proto "création d'optimisation" : voir aussi le prototype existant
  src/prototypes/trajectory-optimization-create-form (Archetype 3 dans
  docs/examples-from-real-prototypes.md).
- Pour un proto "list des optimisations" : voir src/prototypes/size2mix/ref-optimisation-list
  (Archetype 2).
`.trim(),
};

export default manifest;
